import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Heading,
  Input,
  Textarea,
  Select,
  Button,
  Checkbox,
  SimpleGrid,
  Link,
} from "@chakra-ui/react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { MAIN, MAIN_BACKGROUND } from "../colours";
import { createCheckoutSession } from "./api";

// Load Stripe.js once. Publishable keys are safe to expose in the browser.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const inputStyle = {
  bg: "white",
  borderRadius: "none",
  focusBorderColor: MAIN,
  color: MAIN_BACKGROUND,
};

function Label({ children, optional }) {
  return (
    <Text mb="2" color={MAIN} fontSize="sm">
      {children}
      {optional && (
        <Text as="span" opacity={0.6}>
          {" "}
          (optional)
        </Text>
      )}
    </Text>
  );
}

function emptyGuest() {
  return { name: "", dietary: "", allergies: "", notes: "" };
}

export default function BookingForm({ event, availability, availabilityLoading }) {
  const [sittingId, setSittingId] = useState("");
  const [guestCount, setGuestCount] = useState(1);
  const [lead, setLead] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [guests, setGuests] = useState([emptyGuest()]);
  const [seatingRequests, setSeatingRequests] = useState("");
  const [accepted, setAccepted] = useState(
    event.terms.map(() => false)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const selectedSitting = availability && sittingId ? availability[sittingId] : null;
  const remainingForSitting = selectedSitting ? selectedSitting.remaining : event.maxPartySize;
  const maxGuests = Math.max(1, Math.min(event.maxPartySize, remainingForSitting || event.maxPartySize));

  const guestOptions = useMemo(
    () => Array.from({ length: maxGuests }, (_, i) => i + 1),
    [maxGuests]
  );

  const setGuestCountSafe = (n) => {
    const count = Math.max(1, Math.min(maxGuests, Number(n) || 1));
    setGuestCount(count);
    setGuests((prev) => {
      const next = prev.slice(0, count);
      while (next.length < count) next.push(emptyGuest());
      return next;
    });
  };

  const updateGuest = (index, field, value) => {
    setGuests((prev) => prev.map((g, i) => (i === index ? { ...g, [field]: value } : g)));
  };

  const allAccepted = accepted.every(Boolean);

  const validate = () => {
    if (!sittingId) return "Please choose a sitting.";
    if (selectedSitting && selectedSitting.soldOut) return "That sitting is sold out.";
    if (!lead.firstName.trim()) return "Please enter the lead booker's first name.";
    if (!lead.lastName.trim()) return "Please enter the lead booker's surname.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim()))
      return "Please enter a valid email address.";
    if (!lead.phone.trim()) return "Please enter a mobile number.";
    for (let i = 0; i < guests.length; i += 1) {
      if (!guests[i].name.trim()) return `Please enter a name for guest ${i + 1}.`;
    }
    if (!allAccepted) return "Please confirm all three points before paying.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    if (!stripePromise) {
      setError(
        "Payments aren't configured yet (missing Stripe publishable key). Please try again shortly."
      );
      return;
    }

    setSubmitting(true);
    try {
      const { clientSecret: secret } = await createCheckoutSession({
        eventId: event.id,
        sittingId,
        guestCount,
        lead: {
          firstName: lead.firstName.trim(),
          lastName: lead.lastName.trim(),
          email: lead.email.trim(),
          phone: lead.phone.trim(),
        },
        guests,
        seatingRequests: seatingRequests.trim(),
        acceptedTerms: allAccepted,
      });
      setClientSecret(secret);
      // Scroll the payment into view.
      setTimeout(() => {
        const el = document.getElementById("payment");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Once we have a client secret, hand over to Stripe's embedded checkout.
  if (clientSecret) {
    return (
      <Box id="payment" maxW="640px" mx="auto">
        <Heading color={MAIN} size="lg" mb="2">
          Payment
        </Heading>
        <Text color={MAIN} mb="6" fontSize="sm">
          Enter your card details below to secure your booking. Payments are processed
          securely by Stripe.
        </Text>
        <Box bg="white" p={{ base: "2", md: "4" }}>
          <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
            <EmbeddedCheckout />
          </EmbeddedCheckoutProvider>
        </Box>
      </Box>
    );
  }

  const allSoldOut =
    availability &&
    Object.values(availability).length > 0 &&
    Object.values(availability).every((s) => s.soldOut);

  return (
    <Box as="form" onSubmit={handleSubmit} maxW="720px" mx="auto">
      <Heading color={MAIN} size="xl" mb="8">
        Book your places
      </Heading>

      {/* 1. Sitting */}
      <Box mb="10">
        <Label>Choose your sitting</Label>
        <Select
          {...inputStyle}
          maxW="420px"
          placeholder="Please select a sitting…"
          value={sittingId}
          onChange={(e) => {
            const id = e.target.value;
            setSittingId(id);
            // Clamp guests to what's left in the newly chosen sitting.
            const info = availability ? availability[id] : null;
            if (info && info.remaining < guestCount) setGuestCountSafe(info.remaining);
          }}
        >
          {event.sittings.map((s) => {
            const info = availability ? availability[s.id] : null;
            const soldOut = info ? info.soldOut : false;
            return (
              <option key={s.id} value={s.id} disabled={soldOut}>
                {s.time} ({s.label}){soldOut ? " — SOLD OUT" : ""}
              </option>
            );
          })}
        </Select>
        {availabilityLoading && (
          <Text color={MAIN} fontSize="sm" mt="2" opacity={0.7}>
            Checking availability…
          </Text>
        )}
        {allSoldOut && !availabilityLoading && (
          <Text color={MAIN} mt="3">
            Both sittings are now fully booked. Please email{" "}
            <Link href="mailto:happyfigfoods@gmail.com" textDecoration="underline">
              happyfigfoods@gmail.com
            </Link>{" "}
            to join the waiting list.
          </Text>
        )}
      </Box>

      {/* 2. Guests */}
      <Box mb="10">
        <Label>Number of guests</Label>
        <Select
          {...inputStyle}
          maxW="200px"
          value={guestCount}
          onChange={(e) => setGuestCountSafe(e.target.value)}
        >
          {guestOptions.map((n) => (
            <option key={n} value={n}>
              {n} {n === 1 ? "guest" : "guests"}
            </option>
          ))}
        </Select>
      </Box>

      {/* 3. Lead booker */}
      <Box mb="10">
        <Heading color={MAIN} size="md" mb="4">
          Lead booker
        </Heading>
        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="4">
          <Box>
            <Label>First name</Label>
            <Input
              {...inputStyle}
              value={lead.firstName}
              onChange={(e) => setLead({ ...lead, firstName: e.target.value })}
            />
          </Box>
          <Box>
            <Label>Surname</Label>
            <Input
              {...inputStyle}
              value={lead.lastName}
              onChange={(e) => setLead({ ...lead, lastName: e.target.value })}
            />
          </Box>
          <Box>
            <Label>Email</Label>
            <Input
              {...inputStyle}
              type="email"
              value={lead.email}
              onChange={(e) => setLead({ ...lead, email: e.target.value })}
            />
          </Box>
          <Box>
            <Label>Mobile number</Label>
            <Input
              {...inputStyle}
              type="tel"
              value={lead.phone}
              onChange={(e) => setLead({ ...lead, phone: e.target.value })}
            />
          </Box>
        </SimpleGrid>
      </Box>

      {/* 4. Per-guest details */}
      <Box mb="10">
        <Heading color={MAIN} size="md" mb="1">
          Guest details
        </Heading>
        <Text color={MAIN} fontSize="sm" mb="4" opacity={0.85}>
          Please tell us about dietary requirements and allergies for each guest so we can
          cook safely for everyone.
        </Text>
        <Flex direction="column" gap="5">
          {guests.map((g, i) => (
            <Box key={i} border={`1px solid ${MAIN}`} p="5">
              <Text color={MAIN} fontWeight="bold" mb="3">
                Guest {i + 1}
                {i === 0 ? " (lead booker, if attending)" : ""}
              </Text>
              <Box mb="3">
                <Label>Name</Label>
                <Input
                  {...inputStyle}
                  value={g.name}
                  onChange={(e) => updateGuest(i, "name", e.target.value)}
                />
              </Box>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing="3">
                <Box>
                  <Label optional>Dietary requirements</Label>
                  <Input
                    {...inputStyle}
                    placeholder="e.g. vegetarian, vegan, pescatarian"
                    value={g.dietary}
                    onChange={(e) => updateGuest(i, "dietary", e.target.value)}
                  />
                </Box>
                <Box>
                  <Label optional>Food allergies</Label>
                  <Input
                    {...inputStyle}
                    placeholder="e.g. nuts, dairy, gluten"
                    value={g.allergies}
                    onChange={(e) => updateGuest(i, "allergies", e.target.value)}
                  />
                </Box>
              </SimpleGrid>
              <Box mt="3">
                <Label optional>Additional notes</Label>
                <Input
                  {...inputStyle}
                  value={g.notes}
                  onChange={(e) => updateGuest(i, "notes", e.target.value)}
                />
              </Box>
            </Box>
          ))}
        </Flex>
      </Box>

      {/* 5. Seating requests */}
      <Box mb="10">
        <Label optional>Seating requests</Label>
        <Textarea
          {...inputStyle}
          rows={3}
          placeholder="Let us know if your party would like to be seated together, or any access needs."
          value={seatingRequests}
          onChange={(e) => setSeatingRequests(e.target.value)}
        />
      </Box>

      {/* 6. Terms */}
      <Box mb="8">
        <Heading color={MAIN} size="md" mb="4">
          Before you pay
        </Heading>
        <Flex direction="column" gap="3">
          {event.terms.map((term, i) => (
            <Checkbox
              key={i}
              isChecked={accepted[i]}
              onChange={(e) =>
                setAccepted((prev) => prev.map((v, idx) => (idx === i ? e.target.checked : v)))
              }
              colorScheme="green"
              iconColor={MAIN_BACKGROUND}
              sx={{
                ".chakra-checkbox__control": {
                  borderColor: MAIN,
                  borderRadius: 0,
                  bg: "white",
                },
                ".chakra-checkbox__control[data-checked]": { bg: MAIN, borderColor: MAIN },
              }}
            >
              <Text color={MAIN} fontSize="sm">
                {i === event.terms.length - 1 ? (
                  <>
                    I have read and agree to the Happy Fig{" "}
                    <Link href={event.termsUrl} isExternal textDecoration="underline">
                      Terms &amp; Conditions
                    </Link>
                    .
                  </>
                ) : (
                  term
                )}
              </Text>
            </Checkbox>
          ))}
        </Flex>
      </Box>

      {error && (
        <Box bg="#a94442" color="white" p="3" mb="6">
          <Text fontSize="sm">{error}</Text>
        </Box>
      )}

      <Button
        type="submit"
        size="lg"
        w={{ base: "100%", sm: "auto" }}
        bg={MAIN}
        color={MAIN_BACKGROUND}
        border={`2px solid ${MAIN}`}
        borderRadius="none"
        _hover={{ bg: "transparent", color: MAIN }}
        isLoading={submitting}
        loadingText="Preparing secure payment…"
        isDisabled={allSoldOut}
      >
        Continue to payment
      </Button>
    </Box>
  );
}
